import { FontWeight } from '@pasteltech/theme-provider'
import { memo, useMemo } from 'react'
import { styled } from 'styled-components'
import { Autoplay, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { DashIcon, StarIcon } from '../../svg-components'
import { ScreenQuery } from '../../utilities'
import { StrapiImage, StrapiImageData } from '../strapi-image'

const Container = styled.div`
  background: ${({ theme }) => theme.colors.container.highest};
  background-image: url('/images/home-page-showcase-preview-background.png');
  background-position: right center;
  background-repeat: no-repeat;
  display: flex;
`
const LeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 0 0 50%;
  padding: ${({ theme }) => theme.sizes.spacings.xl}px 0;
  gap: ${({ theme }) => theme.sizes.spacings.xxxl}px;
`
const RightContainer = styled.div`
  flex: 0 0 50%;
`
const RatingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--Spacing-xxs, 6px);
`
const Content = styled.div`
  color: ${({ theme }) => theme.colors.primary.mainContrast};
  font-size: ${({ theme }) => theme.sizes.fontSize.heading2}px;
  font-weight: ${FontWeight.bold};
  ${ScreenQuery.mobile} {
    font-size: ${({ theme }) => theme.sizes.fontSize.heading3}px;
  }
`
const Reviewer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizes.spacings.xs}px;
  color: ${({ theme }) => theme.colors.primary.mainContrast};
`
const StyledSwiper = styled(Swiper)`
  width: 100%;
  height: 100%;
  box-sizing: border;
  position: relative;
  .swiper-pagination {
    bottom: ${({ theme }) => theme.sizes.spacings.xxxxxl}px;
    display: flex;
    gap: ${({ theme }) => theme.sizes.spacings.xl}px;
    ${ScreenQuery.desktop} {
      left: 25%;
      width: 50%;
      justify-content: flex-start;
    }
    ${ScreenQuery.tablet} {
      width: 100%;
      justify-content: center;
    }
    ${ScreenQuery.mobile} {
      width: 100%;
      justify-content: center;
    }
  }
  .swiper-pagination-bullet {
    width: ${({ theme }) => theme.sizes.spacings.md}px;
    height: ${({ theme }) => theme.sizes.spacings.md}px;
    background-color: ${({ theme }) => theme.colors.primary.mainContrast};
  }
`
const FeedBack = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.sizes.spacings.xxxxxl}px 0;
  gap: ${({ theme }) => theme.sizes.spacings.xxxxl}px;
  width: 80%;
  margin: 0 auto;
  ${ScreenQuery.mobile} {
    flex-direction: column-reverse;
    width: 95%;
  }
  ${ScreenQuery.tablet} {
    flex-direction: column-reverse;
    width: 80%;
  }
`

type CustomerFeedback = {
  id: string | null
  reviewer: string | null
  rating: number | null
  content: string | null
  image: StrapiImageData
}
type Props = {
  feedbacks: CustomerFeedback[]
}
export const CustomerFeedBackCarousel = memo(({ feedbacks }: Props) => {
  const pagination = useMemo(() => {
    return {
      clickable: true,
    }
  }, [])
  return (
    <Container>
      <StyledSwiper
        pagination={pagination}
        modules={[Autoplay, Pagination]}
        autoplay={{
          delay: 4 * 1000,
          disableOnInteraction: false,
        }}
      >
        {feedbacks.map((feedback) => {
          return (
            <SwiperSlide key={feedback.id}>
              <FeedBack>
                <LeftContainer>
                  <RatingContainer>
                    {[...Array(feedback.rating).keys()].map((idx) => (
                      <StarIcon key={idx} />
                    ))}
                  </RatingContainer>
                  <Content>{feedback.content}</Content>
                  <Reviewer>
                    <DashIcon />
                    {feedback.reviewer}
                  </Reviewer>
                </LeftContainer>
                <RightContainer>
                  <StrapiImage strapiImage={feedback.image} />
                </RightContainer>
              </FeedBack>
            </SwiperSlide>
          )
        })}
      </StyledSwiper>
    </Container>
  )
})
CustomerFeedBackCarousel.displayName = 'CustomerFeedBackCarousel'
